import type { EhrMemberMatch } from "../graphql/resolveEhrMember";
import type { ResolutionState } from "./resolutionState";

/**
 * Auto-navigation for the in-context EHR patient. Two entry points both feed a
 * single durable, per-patient guard so we auto-navigate at most once per Elation
 * patient per browser-tab session:
 *   1. ElationId xwalk match  -> pickXwalkAutoNavTarget (this file)
 *   2. name+dob demographic match on the auto-search (handled in MemberSearchPage)
 *
 * The guard is keyed by Elation patient id so that a user explicitly navigating
 * back for patient A never suppresses auto-nav for a different patient B later in
 * the same session.
 */

/**
 * Choose the member to auto-navigate to for an ElationId xwalk resolution.
 *
 * - `single`: that member (the sole enrollment matched by the xwalk).
 * - `multiple`: the resolve returns one row per enrollment for the SAME person
 *   (the collision guard + exact-pair filter guarantee identity). Auto-navigate
 *   only when EXACTLY ONE is active — "choose the active one". If zero or more
 *   than one enrollment is active, it's ambiguous, so return null and let the
 *   card show the list for the user to choose.
 * - anything else (idle/resolving/none/error): null.
 *
 * Demographic-mismatch and the once-per-patient guard are enforced by the
 * caller — this only selects the candidate.
 */
export function pickXwalkAutoNavTarget(
  state: ResolutionState,
): EhrMemberMatch | null {
  if (state.kind === "single") {
    return state.member._id ? state.member : null;
  }
  if (state.kind === "multiple") {
    const active = state.members.filter((m) => m.isActive === true && !!m._id);
    return active.length === 1 ? active[0] : null;
  }
  return null;
}

/**
 * Whether the in-context (Elation-driven) auto-search should run for the current
 * chart. Mirrors the auto-NAV gating: it fires only when the chart is the
 * FOREGROUND tab (`sourceTabActive`), the xwalk found nothing (`none`), the
 * patient has a name to search, and we haven't already auto-searched this
 * patient id.
 *
 * The `sourceTabActive` gate is the important one: without it, a STICKY
 * background-tab patient hijacks a clinician's manual search — the effect
 * overwrites the form + results with the background patient and re-runs its
 * search on every remount, so a clicked result never "sticks" and no member
 * profile can be reached (the "glitch back to the in-context patient" bug). The
 * two auto-nav effects already gate on `sourceTabActive`; this keeps the
 * auto-search consistent with them.
 */
export function shouldRunInContextAutoSearch(args: {
  resolutionKind: ResolutionState["kind"];
  sourceTabActive: boolean;
  ehrPatientId: string | null;
  alreadySearchedPatientId: string | null;
  hasName: boolean;
}): boolean {
  return (
    args.resolutionKind === "none" &&
    args.sourceTabActive === true &&
    !!args.ehrPatientId &&
    args.alreadySearchedPatientId !== args.ehrPatientId &&
    args.hasName
  );
}

/**
 * Whether the manual-search form should be reset because the FOREGROUND
 * in-context chart switched to a different patient.
 *
 * Same consistency rule as `shouldRunInContextAutoSearch`: a chart in a
 * background tab (`sourceTabActive === false`) must never touch the clinician's
 * form — otherwise a background chart switch (or the bridge re-delivering a
 * sticky patient) wipes what they're typing/searching. Only a genuine change
 * between two non-null patient ids while the chart is foreground resets the
 * form; the initial observation (`prev === null`) is skipped so a
 * router-restored search survives.
 *
 * The caller must additionally skip advancing its "previous foreground patient"
 * ref while not foreground, so a chart that changes while backgrounded still
 * triggers a reset once it returns to the foreground.
 */
export function shouldResetFormForChartChange(args: {
  sourceTabActive: boolean;
  prevInContextPatientId: string | null;
  currentInContextPatientId: string | null;
}): boolean {
  return (
    args.sourceTabActive === true &&
    args.prevInContextPatientId !== null &&
    args.currentInContextPatientId !== null &&
    args.currentInContextPatientId !== args.prevInContextPatientId
  );
}

// Only the MOST-RECENTLY auto-navigated patient is remembered (module-level,
// so it survives the search-page unmount/remount that navigation causes). This
// is deliberately NOT a persistent per-session set: remembering every patient
// ever navigated made auto-nav appear "dead" once you'd opened a chart once
// (re-opening it was silently blocked). Remembering only the last one means:
//   - clicking back to the SAME patient you just landed on won't re-fire (no
//     infinite bounce between search and profile), but
//   - switching to a different chart and back auto-navigates again, as expected.
let lastAutoNavigatedPatientId: string | null = null;

/** True if this patient is the one we just auto-navigated to (skip re-firing). */
export function hasAutoNavigated(ehrPatientId: string): boolean {
  return ehrPatientId === lastAutoNavigatedPatientId;
}

/** Record the patient we just auto-navigated to. */
export function markAutoNavigated(ehrPatientId: string): void {
  lastAutoNavigatedPatientId = ehrPatientId;
}

/**
 * Clear the last-patient guard so the next resolution auto-navigates again.
 *
 * Called on a genuine blur→refocus cycle of the Elation chart tab (the chart
 * lost and regained foreground): re-viewing the chart should re-navigate to its
 * patient even though we already auto-navigated to them earlier in this tab
 * session. It must NOT be called on a plain search→profile→back-to-search
 * bounce (no focus change) — that path keeps the guard so it never re-fires.
 */
export function resetAutoNav(): void {
  lastAutoNavigatedPatientId = null;
}

/**
 * Keep the guard honest as the in-context chart changes: clear it the moment a
 * DIFFERENT patient becomes the in-context chart.
 *
 * This is what makes "switch to another chart and back" auto-navigate again —
 * even when the intermediate chart never auto-navigated (so `markAutoNavigated`
 * never advanced the guard off the first patient), e.g. an eCW patient not in
 * our layer. It's driven by the CURRENT patient id (call it on every context
 * update, incl. on mount) and the guard is module-level, so it works regardless
 * of whether the search page stayed mounted or remounted in between — unlike a
 * per-mount `prev`-based reset, which misses the change when the page remounts
 * fresh at the new patient.
 *
 * A same-patient update is a no-op, so a search→profile→back-to-search bounce
 * (same id) never clears the guard → no auto-nav loop.
 */
export function syncInContextPatient(ehrPatientId: string | null): void {
  if (
    ehrPatientId &&
    lastAutoNavigatedPatientId &&
    ehrPatientId !== lastAutoNavigatedPatientId
  ) {
    lastAutoNavigatedPatientId = null;
  }
}

/** Test-only: clear the module-level guard between cases. */
export function resetAutoNavigatedForTest(): void {
  lastAutoNavigatedPatientId = null;
}

/**
 * Tracks the foreground (`sourceTabActive`) history needed to distinguish a
 * GENUINE blur→refocus of the chart tab from the search-page remount that
 * back-navigation causes.
 *
 * The web-app bridge (`useElationBridge`, instantiated inside the search page)
 * re-initializes `sourceTabActive` to `false` on every mount. So a plain
 * search→profile→back-to-search bounce always looks like `false→true` to a
 * freshly-mounted observer — indistinguishable from a real refocus if we only
 * watched for `false→true` (this was the regression). The reliable tell is that
 * a genuine tab-away first drives a `true→false` transition while the search
 * page stays mounted (the user never leaves the route — only the Chrome tab
 * changes); the bounce never does, because a fresh mount just starts at
 * `false`. So we only re-arm auto-nav on `false→true` AFTER having observed a
 * `true→false` blur (`sawBlur`) within the same mount.
 */
export interface ForegroundTracker {
  prevActive: boolean;
  sawBlur: boolean;
}

/** Seed a tracker from the current foreground value (no blur seen yet). */
export function initForegroundTracker(active: boolean): ForegroundTracker {
  return { prevActive: active, sawBlur: false };
}

/**
 * Advance the tracker for a new `sourceTabActive` value. Returns the next
 * tracker state and whether this transition should re-arm auto-nav — true only
 * on a `false→true` refocus that follows a real `true→false` blur.
 */
export function advanceForegroundTracker(
  tracker: ForegroundTracker,
  active: boolean,
): { tracker: ForegroundTracker; reArm: boolean } {
  if (tracker.prevActive && !active) {
    // Genuine blur: the chart lost foreground while we were watching.
    return { tracker: { prevActive: active, sawBlur: true }, reArm: false };
  }
  if (!tracker.prevActive && active && tracker.sawBlur) {
    // Refocus after a real blur → re-arm (and clear the blur latch).
    return { tracker: { prevActive: active, sawBlur: false }, reArm: true };
  }
  return {
    tracker: { prevActive: active, sawBlur: tracker.sawBlur },
    reArm: false,
  };
}
